import { Button, Group, LoadingOverlay, Menu, Table } from '@mantine/core'
import { IconChevronDown, IconExternalLink, IconPrinter } from '@tabler/icons-react'
import { createReference, formatAddress, formatHumanName } from '@medplum/core'
import {
  type Coverage,
  type CoverageEligibilityResponse,
  type Encounter,
  Identifier,
  type Location,
  Patient,
  type Practitioner,
} from '@medplum/fhirtypes'
import { useCallback, useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { ChartItem } from './PatientChart.utils.tsx'
import { showNotification } from '@mantine/notifications'
import { CreateUpdatePatientModal } from '@/components/ui/CreateUpdatePatientModal'
import { calculateAge, formatDateTime, formatPhone } from '@/utils.ts'
import { modals } from '@mantine/modals'
import { PCPModal } from '@/components/ui/PCPModal'
import { useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { useRecordLocatorEnrichment } from '@/hooks/useRecordLocatorEnrichment'
import { useScrollContext } from '@/context/ScrollContext'
import { usePatientSidePanel } from './PatientSidePanelContext.tsx'
import { ImageZoomModal } from '@/components/ui/ImageZoomModal'
import { logError } from '@/utils/errorUtils'
import { InsuranceSummaryCard } from '@/components/ui/InsuranceSummaryCard'
import {
  CoverageEligibilityOutcomePanel,
  InsuranceCardGallery,
  InsuranceOrgDecisionPanel,
  PatientInsuranceRequestPanel,
} from '@/components/ui'
import type { InsuranceCardSide, InsuranceRank } from '@/types/insuranceTypes'
import {
  convertFromISODate,
  getSubscriberDob,
  getSubscriberMemberId,
  getSubscriberName,
} from '@/utils/coverageUtils'
import { resolveCoverageInsuranceCardImageUrls } from '@/utils/documentReferenceUtils'
import { getCoverageStatus } from '@/utils/eligibilityStatus'
import { useInvalidateInsuranceVerdicts } from '@/hooks/useInsuranceVerdicts'
import {
  fetchLatestPatientInsuranceRequest,
  type PatientInsuranceRequest,
} from '@/utils/insuranceQuestionnaire'
import { useCoveragePersistence } from '@/components/ui/ManageCoverages/useCoveragePersistence.ts'
import { DefaultPharmacySection } from './DefaultPharmacy/DefaultPharmacySection'
import { PatientIdentityVerificationCard } from './PatientIdentityVerificationCard'
import { PhoneLink } from '@/components/ui/PhoneLink'
import { AuthorizationStatusBadge } from './AuthorizationRelease/AuthorizationStatusBadge'
import { AuthorizationReleaseModal } from './AuthorizationRelease'
import { printDemographicsSheet } from '@/utils/demographicsPdf'

type Props = {
  patient: Patient
  item: ChartItem
  refetchPatient: () => void
}

type LocalLocation = {
  name: string
  address?: string
  phone?: string
  fax?: string
  sources?: { address?: string; phone?: string; fax?: string }
}

/**
 * Parses a URL string once, returning its href and hostname only for http(s) URLs
 * (guards against javascript: and other unsafe schemes). Returns null for a malformed
 * or non-http(s) URL so callers render nothing.
 */
function parseSource(url: string): { href: string; hostname: string } | null {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    return { href: url, hostname: parsed.hostname }
  } catch {
    return null
  }
}

/**
 * Clickable provenance icon for an AI-discovered (web-cited) value. Renders nothing
 * for a missing or non-http(s) URL. Parses the URL once and removes the per-cell
 * markup duplication.
 */
function SourceLink({ url }: { url?: string }) {
  if (!url) return null
  const source = parseSource(url)
  if (!source) return null
  return (
    <a
      href={source.href}
      target="_blank"
      rel="noopener noreferrer"
      title={source.hostname}
      className="hover:text-primary ml-1 inline-flex align-middle text-gray-400"
    >
      <IconExternalLink size={14} />
    </a>
  )
}

export const Demographics = ({ patient, refetchPatient }: Props) => {
  const [locations, setLocations] = useState<LocalLocation[]>([])
  const [providers, setProviders] = useState<LocalLocation[]>([])
  const [isLoadingLocations, setIsLoadingLocations] = useState(true)
  const [isLoadingProviders, setIsLoadingProviders] = useState(true)
  const [isLoadingPcp, setIsLoadingPcp] = useState(true)
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false)
  const [isPCPModalOpen, setIsPCPModalOpen] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [pcpPractitioner, setPcpPractitioner] = useState<Practitioner | null>(null)
  const [patientSSN, setPatientSSN] = useState<string | null>(null)
  const [patientMothersMaidenName, setPatientMothersMaidenName] = useState<string | null>(null)
  const [patientSexAtBirth, setPatientSexAtBirth] = useState<string | null>(null)
  const [patientPreferredLanguage, setPatientPreferredLanguage] = useState<string | null>(null)
  const [patientRace, setPatientRace] = useState<string | null>(null)
  const [ethnicity, setEthnicity] = useState<string | null>(null)
  const [secondEthnicity, setSecondEthnicity] = useState<string | null>(null)
  const [showSsn, setShowSsn] = useState(false)
  const [recordView, setRecordView] = useState<'saved' | 'ai'>('ai')
  const {
    data: enrichment,
    loading: isEnriching,
    progress,
    refresh: refreshEnrichment,
  } = useRecordLocatorEnrichment(patient.id, recordView === 'ai')

  const [initialInsuranceRankToEdit, setInitialInsuranceRankToEdit] = useState<
    InsuranceRank | undefined
  >(undefined)

  const medplum = useMedplum()
  const { panelEventVersions } = usePatientSidePanel()
  const patientEditedVersion = panelEventVersions['patient:edited'] || 0
  const middleware = useMiddlewareClient()
  const orpcClient = useClient()
  const middlewareClient = middleware as unknown as {
    get: <T = unknown>(url: string) => Promise<{ result: T }>
    post: <T = unknown>(url: string, body?: unknown) => Promise<{ result: T }>
  }
  const { getInsuranceDisplayData } = useCoveragePersistence({
    medplum,
    middlewareClient: middleware,
    patientId: patient.id,
  })
  const [primaryInsurance, setPrimaryInsurance] = useState<Coverage | null>(null)
  const [primaryCoverageResponse, setPrimaryCoverageResponse] =
    useState<CoverageEligibilityResponse | null>(null)
  const [patientInsuranceRequest, setPatientInsuranceRequest] =
    useState<PatientInsuranceRequest | null>(null)
  const [primaryInsuranceCardPreviews, setPrimaryInsuranceCardPreviews] = useState<
    Record<InsuranceCardSide, string | null>
  >({
    front: null,
    back: null,
  })
  const [primaryInsuranceCardLoading, setPrimaryInsuranceCardLoading] = useState(false)
  const [zoomedInsuranceCardUrl, setZoomedInsuranceCardUrl] = useState<string | null>(null)
  const [isPrintingSheet, setIsPrintingSheet] = useState(false)
  const COVERAGE_VERSION_TAG_SYSTEM = 'https://behuman.health/coverage-version-id'
  const coverageVersionId = primaryInsurance?.meta?.versionId
  const responseVersionId = primaryCoverageResponse?.meta?.tag?.find(
    (t) => t.system === COVERAGE_VERSION_TAG_SYSTEM,
  )?.code
  const isResponseStale = Boolean(
    primaryCoverageResponse && coverageVersionId && responseVersionId !== coverageVersionId,
  )
  const displayedPrimaryInsuranceResponse =
    isResponseStale || primaryCoverageResponse == null ? null : primaryCoverageResponse
  const displayedPrimaryInsuranceCoverageStatus = getCoverageStatus(
    displayedPrimaryInsuranceResponse,
  )
  const displayedPrimaryInsuranceLastCheckDate = isResponseStale
    ? null
    : primaryCoverageResponse?.created || primaryCoverageResponse?.meta?.lastUpdated || null

  // A new eligibility check changes this patient's verdict wherever the console shows it.
  const invalidateInsuranceVerdicts = useInvalidateInsuranceVerdicts()

  const resetPrimaryInsuranceApprovalState = useCallback(() => {
    setPrimaryCoverageResponse(null)
  }, [])

  const loadPrimaryCoverageApprovalState = useCallback(
    async (coverage: Coverage | null) => {
      if (!patient.id || !coverage?.id) {
        setPrimaryCoverageResponse(null)
        return
      }

      const coverageReference = `Coverage/${coverage.id}`
      const patientReference = `Patient/${patient.id}`

      const [requests, responses] = await Promise.all([
        medplum.searchResources(
          'CoverageEligibilityRequest',
          `patient=${patientReference}&_sort=-_lastUpdated&_count=50`,
        ),
        medplum.searchResources(
          'CoverageEligibilityResponse',
          `patient=${patientReference}&_sort=-_lastUpdated&_count=50`,
        ),
      ])

      const request =
        requests.find((item) =>
          item.insurance?.some((insurance) => insurance.coverage?.reference === coverageReference),
        ) || null

      const response = request
        ? responses.find(
            (item) => item.request?.reference === createReference(request).reference,
          ) || null
        : null

      setPrimaryCoverageResponse(response)
    },
    [medplum, patient.id],
  )

  /**
   * The patient's primary coverage, or null when they have none. Throws when the
   * read fails, so callers can tell "no insurance" from "we could not find out" —
   * a distinction `primaryInsurance` state cannot make, since it holds null for
   * both (PD-1523).
   */
  const fetchPrimaryCoverage = useCallback(async (): Promise<Coverage | null> => {
    const { result } = await middlewareClient.get<{ primary?: Coverage }>(
      `/insurance/patient/${patient.id}/coverages`,
    )
    return result?.primary || null
  }, [middlewareClient, patient.id])

  const refreshPrimaryInsuranceState = useCallback(async () => {
    try {
      medplum.invalidateSearches('CoverageEligibilityRequest')
      medplum.invalidateSearches('CoverageEligibilityResponse')
      const nextPrimaryInsurance = await fetchPrimaryCoverage()
      setPrimaryInsurance(nextPrimaryInsurance)
      await loadPrimaryCoverageApprovalState(nextPrimaryInsurance)
    } catch {
      setPrimaryInsurance(null)
      resetPrimaryInsuranceApprovalState()
    }
  }, [
    fetchPrimaryCoverage,
    loadPrimaryCoverageApprovalState,
    medplum,
    resetPrimaryInsuranceApprovalState,
  ])

  const refreshPatientInsuranceRequest = useCallback(async () => {
    try {
      setPatientInsuranceRequest(
        await fetchLatestPatientInsuranceRequest(medplum, patient.id as string),
      )
    } catch {
      setPatientInsuranceRequest(null)
    }
  }, [medplum, patient.id])

  useEffect(() => {
    void refreshPrimaryInsuranceState()
    void refreshPatientInsuranceRequest()
  }, [refreshPatientInsuranceRequest, refreshPrimaryInsuranceState])

  useEffect(() => {
    let active = true

    const loadPrimaryInsuranceCards = async (): Promise<void> => {
      if (!primaryInsurance) {
        setPrimaryInsuranceCardPreviews({ front: null, back: null })
        setPrimaryInsuranceCardLoading(false)
        return
      }

      setPrimaryInsuranceCardLoading(true)
      try {
        const previews = await resolveCoverageInsuranceCardImageUrls(medplum, primaryInsurance)
        if (active) {
          setPrimaryInsuranceCardPreviews(previews)
        }
      } finally {
        if (active) {
          setPrimaryInsuranceCardLoading(false)
        }
      }
    }

    void loadPrimaryInsuranceCards()

    return () => {
      active = false
    }
  }, [medplum, primaryInsurance])

  const handlePrimaryInsuranceEligibilityCheck = async () => {
    if (!patient.id || !primaryInsurance) {
      showNotification({
        color: 'red',
        message: 'Primary insurance not found',
      })
      return
    }

    if (!primaryInsurance.id) {
      showNotification({
        color: 'red',
        message: 'Primary insurance is missing an id',
      })
      return
    }

    try {
      const { result } = await middlewareClient.post<CoverageEligibilityResponse>(
        '/insurance/check-eligibility',
        {
          patientId: patient.id,
          coverageId: primaryInsurance.id,
        },
      )

      setPrimaryCoverageResponse(result)
      void invalidateInsuranceVerdicts()

      modals.open({
        title: 'Eligibility Check Result',
        size: 'lg',
        children: (
          <div
            className="text-sm"
            dangerouslySetInnerHTML={{
              __html: result.disposition || 'No eligibility details returned.',
            }}
          />
        ),
      })
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Eligibility check failed',
      })
    }
  }

  const { scrollToTop } = useScrollContext()

  useEffect(() => {
    scrollToTop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (patientEditedVersion > 0) {
      void refreshPrimaryInsuranceState()
      void refreshPatientInsuranceRequest()
    }
  }, [patientEditedVersion, refreshPatientInsuranceRequest, refreshPrimaryInsuranceState])

  useEffect(() => {
    fetchNeededData()
    if (patient?.identifier) {
      const ssnIdentifier: Identifier | undefined = patient.identifier.find(
        (item) => item.system === 'http://hl7.org/fhir/sid/us-ssn',
      )
      if (ssnIdentifier || ((ssnIdentifier as Identifier | undefined)?.value?.length || 0) > 7) {
        const [a, b, c, d, e, ...rest] = ssnIdentifier!.value as string
        setPatientSSN(`${a}${b}${c}-${d}${e}-${rest.join('')}`)
        // setPatientSSN(ssnIdentifier.value || null)
      }
    }
    if (patient?.extension) {
      const mothersMaidenNameExtension = patient.extension.find(
        (ext) => ext.url === 'http://hl7.org/fhir/StructureDefinition/patient-mothersMaidenName',
      )
      const sexAtBirthExtension = patient.extension.find(
        (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
      )
      if (mothersMaidenNameExtension && mothersMaidenNameExtension.valueString) {
        setPatientMothersMaidenName(mothersMaidenNameExtension.valueString)
      }

      if (sexAtBirthExtension && sexAtBirthExtension.valueCode) {
        setPatientSexAtBirth(sexAtBirthExtension.valueCode)
      }
      const ethnicityExtension = patient?.extension?.find(
        (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',
      )
      if (ethnicityExtension && ethnicityExtension?.extension) {
        const primaryEthnicityExtensions = ethnicityExtension?.extension.filter(
          (ext) => ext.url === 'detailed-ethnicity',
        )
        const secondEthnicityExtensions = ethnicityExtension?.extension.filter(
          (ext) => ext.url === 'detailed-second-ethnicity',
        )
        setEthnicity(
          primaryEthnicityExtensions?.map((i) => i.valueCoding?.display).join(', ') || ' -',
        )
        setSecondEthnicity(
          secondEthnicityExtensions?.map((i) => i.valueCoding?.display).join(', ') || ' -',
        )
      }
    }
    if (patient?.communication) {
      const preferredLanguage = patient.communication.find(
        (comm) => comm.language && comm.preferred === true,
      )
      if (preferredLanguage?.language?.coding?.[0]) {
        setPatientPreferredLanguage(preferredLanguage?.language.coding[0].display || '')
      }
    }
    if (patient?.extension) {
      const raceExtension = patient.extension.find(
        (ext) => ext.url === 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',
      )
      if (raceExtension && raceExtension?.extension) {
        setPatientRace(
          raceExtension?.extension.map((i) => i.valueCoding?.display).join(', ') || ' -',
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, patient])

  const fetchNeededData = () => {
    const patientRef = `Patient/${patient.id}`

    setIsLoadingLocations(true)
    setIsLoadingProviders(true)

    void medplum
      .searchResources('Location', `_source=${patientRef}&_count=500`)
      .catch((error) => {
        logError('patient', 'Fetch locations', error)
        return [] as Location[]
      })
      .then((locations: Location[]) => {
        const locationMap = new Map<string, { name: string; address?: string; phone?: string }>()

        locations.forEach((location) => {
          const name = location.name || ''
          const existingLocation = locationMap.get(name)
          const address = location.address ? formatAddress(location.address) : undefined
          const phone = location.telecom?.find((t) => t.system === 'phone')?.value

          if (existingLocation) {
            locationMap.set(name, {
              name,
              address: address || existingLocation.address,
              phone: phone || existingLocation.phone,
            })
          } else {
            locationMap.set(name, { name, address, phone })
          }
        })

        setLocations(Array.from(locationMap.values()))
      })
      .finally(() => {
        setIsLoadingLocations(false)
      })

    medplum
      .searchResources('Encounter', `subject=${patientRef}&_count=500`)
      .catch((error) => {
        logError('patient', 'Fetch encounters', error)
        return [] as Encounter[]
      })
      .then((encounters) => {
        const providerMap = new Map<string, LocalLocation>()

        encounters.forEach((encounter) => {
          // Extract contained Location once per encounter
          const containedLocation = encounter.contained?.find(
            (r): r is Location => r.resourceType === 'Location',
          )

          const address =
            containedLocation?.address?.text ??
            (containedLocation?.address ? formatAddress(containedLocation.address) : undefined)

          const phone = containedLocation?.telecom?.find((t) => t.system === 'phone')?.value

          // Attach location info to each practitioner in this encounter
          encounter.participant?.forEach((participant) => {
            const name = participant.individual?.display
            if (!name) return

            if (!providerMap.has(name)) {
              providerMap.set(name, {
                name,
                address,
                phone,
              })
            }
          })
        })

        setProviders(Array.from(providerMap.values()))
      })

      .catch((error) => {
        logError('patient', 'Fetch providers', error)
      })
      .finally(() => {
        setIsLoadingProviders(false)
      })
  }

  useEffect(() => {
    if (!patient.id) return
    setIsLoadingPcp(true)
    middleware
      .get('/patient/{patientId}/pcp', { patientId: patient.id })
      .then((p) => setPcpPractitioner((p as Practitioner) || null))
      .catch(() => setPcpPractitioner(null))
      .finally(() => setIsLoadingPcp(false))
  }, [patient.id, patient.generalPractitioner, middleware])

  /**
   * PD-1523 — prints the demographic & contact sheet from what the chart holds;
   * anything beHuman does not store is left off the sheet entirely.
   *
   * The provider is the one the patient was last SEEN by, not the PCP (Alina).
   * Both it and the coverage are read on CLICK rather than reused from the
   * chart's state: they cost a request only a print needs, and the sheet then
   * reflects the record as it stands now.
   *
   * The two failures are handled differently on purpose. A provider we cannot
   * resolve drops its section — "never seen here" is a normal, common state, so
   * an absent provider says nothing false. Coverage is the opposite: the chart's
   * `primaryInsurance` holds null both for "no insurance" and for "the request
   * is still in flight or failed", and because the sheet omits empty sections, a
   * failed read would print a document that silently denies insurance the patient
   * actually has. So a coverage failure aborts the print instead.
   */
  const handlePrintDemographics = () => {
    setIsPrintingSheet(true)
    void (async () => {
      try {
        const [coverage, { provider }] = await Promise.all([
          fetchPrimaryCoverage(),
          orpcClient.getLastSeenProvider({ patientId: patient.id as string }).catch((error) => {
            logError('patient', 'Resolve last seen provider for demographics sheet', error)
            return { provider: null }
          }),
        ])
        await printDemographicsSheet(patient, coverage ?? undefined, provider ?? undefined)
      } catch (error) {
        logError('patient', 'Print demographics sheet', error)
        showNotification({ color: 'red', message: 'Could not prepare the demographics sheet.' })
      } finally {
        setIsPrintingSheet(false)
      }
    })()
  }

  let memberId = ''
  if (primaryInsurance) {
    const { memberId: memberIdString } = getInsuranceDisplayData(primaryInsurance)
    memberId = memberIdString
  }
  const primaryInsuranceCard = primaryInsurance && (
    <div className="flex flex-col gap-3">
      <PatientInsuranceRequestPanel
        request={patientInsuranceRequest}
        onDismissed={() => {
          void refreshPatientInsuranceRequest()
        }}
      />
      <InsuranceSummaryCard
        insurance={{
          provider:
            Array.isArray(primaryInsurance.payor) && primaryInsurance.payor[0]?.display
              ? primaryInsurance.payor[0].display
              : '-',
          memberId,
          rank: 'Primary',
          type:
            (primaryInsurance.type && primaryInsurance.type.text) ||
            (primaryInsurance.relationship &&
              Array.isArray(primaryInsurance.relationship.coding) &&
              primaryInsurance.relationship.coding[0]?.display) ||
            '-',
          showSubscriberDetails:
            primaryInsurance.relationship?.coding?.[0]?.code?.toLowerCase() !== 'self',
          subscriberName: getSubscriberName(primaryInsurance) || '-',
          subscriberDob: convertFromISODate(getSubscriberDob(primaryInsurance)) || '-',
          subscriberMemberId: getSubscriberMemberId(primaryInsurance) || '-',
          coverageStatus: displayedPrimaryInsuranceCoverageStatus,
          lastCheckDate: displayedPrimaryInsuranceLastCheckDate
            ? formatDateTime(displayedPrimaryInsuranceLastCheckDate, '-', true, true)
            : '-',
        }}
        onEdit={() => {
          setInitialInsuranceRankToEdit('Primary')
          setIsPatientModalOpen(true)
        }}
        onCheckEligibility={handlePrimaryInsuranceEligibilityCheck}
        showReviewActions={false}
      />
      <div className="rounded-2xl border border-[#E6EDF5] bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <InsuranceCardGallery
          previews={primaryInsuranceCardPreviews}
          loading={primaryInsuranceCardLoading}
          variant="compact"
          helperText="Front and back images attached to the primary insurance."
          onImageClick={(url) => setZoomedInsuranceCardUrl(url)}
        />
      </div>
      <InsuranceOrgDecisionPanel
        patientId={patient.id as string}
        coverage={primaryInsurance}
        onSaved={() => {
          void refreshPrimaryInsuranceState()
        }}
      />
      <CoverageEligibilityOutcomePanel
        latestEligibilityResponse={primaryCoverageResponse}
        isEligibilityStale={isResponseStale}
        className="max-h-100 overflow-y-auto rounded-2xl border"
      />
    </div>
  )

  const recordLocationsToShow: LocalLocation[] =
    recordView === 'ai' ? (enrichment?.locations ?? []) : locations
  const providersToShow: LocalLocation[] =
    recordView === 'ai' ? (enrichment?.providers ?? []) : providers
  const showEnrichmentLoading = recordView === 'ai' && isEnriching

  return (
    // Container query (not viewport): the insurance card sits beside the
    // demographics while the actual column is ≥730px wide, else wraps below it
    // — so a call's video panel narrowing the column can't clip the fixed 430px
    // card (PD-1068). `gap-y-6` spaces the wrapped card with no horizontal
    // gutter side-by-side, keeping the standalone wide chart unchanged.
    <div className="@container flex flex-wrap gap-y-6">
      <div className="flex-1 p-4 pt-0">
        <ImageZoomModal
          opened={Boolean(zoomedInsuranceCardUrl)}
          imageUrl={zoomedInsuranceCardUrl}
          title="Insurance Card Preview"
          imageAlt="Insurance card"
          onClose={() => setZoomedInsuranceCardUrl(null)}
        />
        <CreateUpdatePatientModal
          isOpen={isPatientModalOpen}
          onClose={() => {
            setIsPatientModalOpen(false)
            setInitialInsuranceRankToEdit(undefined)
          }}
          patient={patient}
          initialInsuranceRankToEdit={initialInsuranceRankToEdit}
          onInsuranceChanged={() => void refreshPatientInsuranceRequest()}
          successCallback={() => {
            medplum.invalidateSearches('Patient')
            void refreshPrimaryInsuranceState()
            void refreshPatientInsuranceRequest()
            refetchPatient()
          }}
        />
        <div className="mt-6 flex">
          <div className="flex w-[120px] items-center text-sm font-bold text-[#7D7987]">Photo:</div>
          <div className="flex items-center gap-2">
            {patient.photo?.[0]?.url && (
              <img
                src={patient.photo?.[0]?.url}
                alt="patient-photo"
                className="h-[64px] w-[48px] cursor-pointer rounded-lg border border-slate-200 object-cover hover:opacity-70"
                onClick={() => {
                  modals.open({
                    onClose: modals.closeAll,
                    children: (
                      <img src={patient.photo?.[0]?.url} alt="patient-photo" className="w-full" />
                    ),
                  })
                }}
              />
            )}
            {!patient.photo?.[0]?.url && (
              <div className="h-[64px] w-[48px] rounded-lg bg-gray-200" />
            )}
          </div>
          {/* PD-1523 — sits in the photo row so the print action costs no extra
              vertical space above the demographics table. */}
          <div className="ml-auto">
            <Button
              variant="outline"
              size="xs"
              loading={isPrintingSheet}
              leftSection={<IconPrinter size={14} />}
              onClick={handlePrintDemographics}
              className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] hover:from-[#FFFFFF]"
            >
              Print
            </Button>
          </div>
        </div>
        <div className="mt-2 flex flex-col gap-6 xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1">
            <table className="mt-3 text-base text-sm">
              <tbody>
                <tr className="h-[34px]">
                  <td width={120} className="text-sm font-bold text-[#7D7987]">
                    Date of birth:
                  </td>
                  <td>
                    {patient.birthDate
                      ? `${formatDateTime(patient.birthDate || '', '/', false, true)} (${calculateAge(patient.birthDate)} years)`
                      : 'Unknown'}
                  </td>
                  <td width={190} className="pl-[30px] text-sm font-bold text-[#7D7987]">
                    Mother's Maiden Name:
                  </td>
                  <td colSpan={2}>{patientMothersMaidenName || '-'}</td>
                </tr>
                <tr className="h-[34px]">
                  <td width={80} className="text-sm font-bold text-[#7D7987]">
                    Gender:
                  </td>
                  <td>
                    {patient.gender?.charAt(0).toUpperCase()}
                    {patient.gender?.slice(1)}
                  </td>
                  <td width={50} className="pl-[30px] text-sm font-bold text-[#7D7987]">
                    SSN:
                  </td>
                  <td>
                    {patientSSN ? (
                      <button
                        type="button"
                        onClick={() => setShowSsn((v) => !v)}
                        aria-label={showSsn ? 'Hide SSN' : 'Show SSN'}
                        className="cursor-pointer border-0 bg-transparent p-0 text-left font-mono decoration-dotted"
                      >
                        {showSsn ? patientSSN : patientSSN.replace(/\d/g, '*')}
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td width={120} className="text-sm font-bold text-[#7D7987]">
                    Sex at Birth:
                  </td>
                  <td>{patientSexAtBirth || '-'}</td>
                  <td width={100} className="pl-[30px] text-sm font-bold text-[#7D7987]">
                    Ethnicity:
                  </td>
                  <td>{ethnicity || '-'}</td>
                </tr>
                <tr className="h-[34px]">
                  <td className="text-sm font-bold text-[#7D7987]">Phone:</td>
                  <td>
                    {formatPhone(
                      patient.telecom?.find((item) => item.system === 'phone')?.value || '',
                    )}
                  </td>
                  <td
                    width={150}
                    className="pl-[30px] text-sm font-bold text-[#7D7987]"
                    colSpan={1}
                  >
                    Second Ethnicity:
                  </td>
                  <td colSpan={2}>{secondEthnicity || '-'}</td>
                </tr>
                <tr className="h-[34px] align-top">
                  <td className="text-sm font-bold text-[#7D7987]">Address:</td>
                  <td>
                    {patient.address
                      ?.filter((item) => item.use !== 'old')
                      ?.map((a, index) => (
                        <p key={index} className="mb-2 last:mb-0">
                          {formatAddress(a)}
                        </p>
                      ))}
                  </td>
                  <td width={60} className="pl-[30px] text-sm font-bold text-[#7D7987]">
                    Race:
                  </td>
                  <td>{patientRace || '-'}</td>
                </tr>
                <tr className="h-[34px]">
                  <td width={80} className="text-sm font-bold text-[#7D7987]">
                    Email:
                  </td>
                  <td>{patient.telecom?.find((item) => item.system === 'email')?.value || ''}</td>
                  <td width={80} className="pl-[30px] text-sm font-bold text-[#7D7987]">
                    Preferred Language:
                  </td>
                  <td>{patientPreferredLanguage || '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <PatientIdentityVerificationCard patient={patient} onPatientUpdated={refetchPatient} />
        <div className="mt-6 flex items-center gap-3">
          <p className="text-2xl text-base">Primary Care Physician (PCP)</p>
          <Button
            variant="outline"
            size="xs"
            onClick={() => setIsPCPModalOpen(true)}
            className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] hover:from-[#FFFFFF]"
          >
            {pcpPractitioner ? 'Edit' : 'Add'}
          </Button>
        </div>
        <div className="relative" style={{ minHeight: isLoadingPcp ? 80 : undefined }}>
          <LoadingOverlay visible={isLoadingPcp} />
          {pcpPractitioner ? (
            <table className="mt-2 text-sm">
              <tbody>
                {pcpPractitioner.name?.[0] && (
                  <tr className="h-[34px]">
                    <td width={80} className="text-sm font-bold text-[#7D7987]">
                      Name:
                    </td>
                    <td>{formatHumanName(pcpPractitioner.name[0])}</td>
                  </tr>
                )}
                {pcpPractitioner.telecom?.find((t) => t.system === 'phone')?.value && (
                  <tr className="h-[34px]">
                    <td width={80} className="text-sm font-bold text-[#7D7987]">
                      Phone:
                    </td>
                    <td>
                      <PhoneLink
                        phone={pcpPractitioner.telecom.find((t) => t.system === 'phone')!.value}
                      />
                    </td>
                  </tr>
                )}
                {pcpPractitioner.address?.[0] && (
                  <tr className="h-[34px]">
                    <td width={80} className="text-sm font-bold text-[#7D7987]">
                      Address:
                    </td>
                    <td>{formatAddress(pcpPractitioner.address[0])}</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : !isLoadingPcp ? (
            <p className="mt-2 text-sm text-gray-400">No PCP information added.</p>
          ) : null}
        </div>

        <PCPModal
          isOpen={isPCPModalOpen}
          onClose={() => setIsPCPModalOpen(false)}
          patient={patient}
          existingPractitioner={pcpPractitioner}
          onSuccess={refetchPatient}
        />

        <DefaultPharmacySection patient={patient} refetchPatient={refetchPatient} />

        <Group justify="flex-start" align="center" gap="lg" className="mt-6">
          <Group gap="sm" align="center">
            <p className="text-2xl text-base">Record Locations</p>
            <Button
              variant="outline"
              size="xs"
              onClick={() => setIsAuthModalOpen(true)}
              className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] hover:from-[#FFFFFF]"
            >
              Request Records
            </Button>
            <AuthorizationStatusBadge patientId={patient.id ?? ''} />
          </Group>
          <Group gap="sm" align="center">
            <Menu
              position="bottom-start"
              width="target"
              offset={0}
              shadow="md"
              radius="md"
              withinPortal
            >
              <Menu.Target>
                <Button
                  size="xs"
                  variant="default"
                  radius="md"
                  fw={500}
                  px={12}
                  rightSection={<IconChevronDown size={14} style={{ color: '#868e96' }} />}
                >
                  {recordView === 'ai' ? 'Enriched' : 'TEFCA'}
                </Button>
              </Menu.Target>
              <Menu.Dropdown p={0}>
                {recordView !== 'ai' && (
                  <Menu.Item
                    fz="xs"
                    fw={500}
                    style={{ height: 30, padding: '0 12px' }}
                    onClick={() => setRecordView('ai')}
                  >
                    Enriched
                  </Menu.Item>
                )}
                {recordView !== 'saved' && (
                  <Menu.Item
                    fz="xs"
                    fw={500}
                    style={{ height: 30, padding: '0 12px' }}
                    onClick={() => setRecordView('saved')}
                  >
                    TEFCA
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
            {recordView === 'ai' && (
              <Button size="xs" variant="subtle" onClick={refreshEnrichment} disabled={isEnriching}>
                Rerun
              </Button>
            )}
          </Group>
        </Group>

        <AuthorizationReleaseModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          patient={patient}
        />

        <div
          className="relative"
          style={{ minHeight: isLoadingLocations || showEnrichmentLoading ? 120 : undefined }}
        >
          {/* Spinner only for the raw FHIR load; once enrichment is running we show the
              progress bar instead (starting at 0% before the first progress poll lands). */}
          <LoadingOverlay visible={isLoadingLocations && !showEnrichmentLoading} />
          {showEnrichmentLoading && (
            <div className="py-6 text-center">
              <div className="mx-auto h-3 w-full max-w-md rounded-full bg-gray-200">
                <div
                  className="bg-primary h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress?.pct ?? 0}%` }}
                />
              </div>
              <p className="mt-2 text-sm text-[#7D7987]">
                Enriching records… {progress?.pct ?? 0}%
              </p>
            </div>
          )}
          <Table mt="md" style={{ tableLayout: 'fixed', width: '100%' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '26%' }}>Name</Table.Th>
                <Table.Th>Address</Table.Th>
                <Table.Th style={{ width: 160 }}>Phone</Table.Th>
                <Table.Th style={{ width: 160 }}>Fax</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recordLocationsToShow.map((location) => (
                <Table.Tr key={location.name}>
                  <Table.Td>{location.name}</Table.Td>
                  <Table.Td>
                    {location.address || '-'}
                    <SourceLink url={location.sources?.address} />
                  </Table.Td>
                  <Table.Td className="whitespace-nowrap">
                    <PhoneLink phone={location.phone} />
                    <SourceLink url={location.sources?.phone} />
                  </Table.Td>
                  <Table.Td className="whitespace-nowrap">
                    {location.fax || '-'}
                    <SourceLink url={location.sources?.fax} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>

        <p className="mt-6 text-2xl text-base">Providers</p>

        <div className="relative" style={{ minHeight: isLoadingProviders ? 120 : undefined }}>
          {/* No enrichment spinner here: provider NPI lookup finishes long before the
              (slower) location web-search, and both land together at run end — so a
              spinner would just sit spinning. Show the current providers instead. */}
          <LoadingOverlay visible={isLoadingProviders} />
          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '26%' }}>Name</Table.Th>
                <Table.Th>Address</Table.Th>
                <Table.Th style={{ width: 160 }}>Phone</Table.Th>
                <Table.Th style={{ width: 160 }}>Fax</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {providersToShow.length > 0 ? (
                providersToShow.map((provider, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{provider.name}</Table.Td>
                    <Table.Td>{provider.address || '-'}</Table.Td>
                    <Table.Td>
                      <PhoneLink phone={provider.phone} />
                    </Table.Td>
                    <Table.Td>{provider.fax || '-'}</Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={4}>No providers found</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </div>
      </div>

      {primaryInsuranceCard && (
        // Full-width + no pull-up when wrapped below; the 430px side-by-side
        // layout with the -49px top alignment applies only once the column is
        // wide enough (matches the container-query threshold above). -49 =
        // the original -60 tuned against the pinned 76px header reservation,
        // minus the 11px the content rose when the reservation became the
        // measured ~65px (PatientChartActionBar's ResizeObserver).
        <div className="relative top-0 z-20 w-full @min-[730px]:top-[-49px] @min-[730px]:w-[430px] @min-[730px]:shrink-0">
          <div className="rounded-3xl border border-[#E7EEF6] bg-[#F8FBFF] p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="mb-4 text-xs font-semibold tracking-[0.18em] text-[#7D8EA3] uppercase">
              Primary Insurance
            </div>
            {primaryInsuranceCard}
          </div>
        </div>
      )}
    </div>
  )
}
